import L from "@/modules/login/Login.module.css";

import type { Action } from "@/components/Action.tsx";
import { IsoCerts } from "@/components/IsoCerts.tsx";
import { WButton, type WButtonProps } from "@/components/button/WButton.tsx";
import { FormSubmitButton } from "@/components/forms/FormSubmitButton.tsx";
import { WHookForm } from "@/components/forms/WHookForm.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { didStoreReferralCodeAtom } from "@/modules/billing/referral/referralLogic.ts";
import { notifyError } from "@/modules/error/errorReporting.ts";
import { LocaleFullSelector } from "@/modules/l10n/LocaleButton.tsx";
import { languagesEnabledAtom } from "@/modules/l10n/i18n.store.ts";
import { ApiKeyLoginForm } from "@/modules/login/ApiKeyLogin.tsx";
import { InputRow } from "@/modules/login/InputRow.tsx";
import { LoginBox, LoginLayout } from "@/modules/login/LoginLayout.tsx";
import { Auth0LoginForm } from "@/modules/login/auth0/Auth0Login.tsx";
import { auth0ProviderAtom } from "@/modules/login/auth0/auth0Provider.ts";
import { authProviderAtom, type AuthProvider } from "@/modules/login/authProvider.ts";
import { getCurrentPath } from "@/modules/main/urls.ts";
import { errorToReactMessage } from "@/modules/notifications/errorToReactMessage.tsx";
import { useThemeProps } from "@/modules/theme/useTheme.ts";
import { mcn } from "@/utils/baseProps.ts";
import { cn } from "@/utils/classNames.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { useRouter } from "@tanstack/react-router";
import { useAtomValue, useStore } from "jotai/react";
import React, { Suspense, useMemo, useState, type ReactElement, type ReactNode } from "react";
import { Link } from "react-aria-components";
import { useForm, type SubmitHandler } from "react-hook-form";

// Lazy load chat loader since it pulls in a bunch of dependencies (eg. query client for user data)
const WChatLoader = React.lazy(() => import("@/modules/support/WChatProvider.tsx"));

function BackButton(props: WButtonProps) {
    return (
        <div className="pt-5 pb-1 text-center">
            <WButton {...mcn(L.More, props)}>
                <Trans>Back</Trans>
            </WButton>
        </div>
    );
}

function ForgotPassword({
    onBack,
    onPostSubmit,
}: {
    provider: AuthProvider;
    onBack: () => void;
    onPostSubmit: (username: string) => void;
}) {
    interface ForgotInputs {
        email: string;
    }

    //#region Hooks
    const { t } = useLingui();
    const form = useForm<ForgotInputs>({
        defaultValues: {
            email: "",
        },
    });
    const authProvider = useAtomValue(auth0ProviderAtom);
    const [error, setError] = useState<ReactNode>(null);
    //#endregion

    const onSubmit: SubmitHandler<ForgotInputs> = async ({ email }) => {
        try {
            setError(null);
            await authProvider.forgotPassword({ email });
            onPostSubmit(email);
        } catch (error) {
            notifyError(error, { tags: ["ForgotPassword"] });
            setError(errorToReactMessage(error));
        }
    };

    return (
        <LoginBox title={t`Forgot password`}>
            <p>
                <Trans>Please enter your email address. We will send you an email to reset your password.</Trans>
            </p>

            <WHookForm form={form} onSubmit={onSubmit}>
                <InputRow
                    name="email"
                    type="email"
                    label={t({ comment: "Field label", message: "Email" })}
                    isRequired
                    autoFocus
                />

                <FormSubmitButton size="lg" fontSize="font-size-subtitle" className={L.Button}>
                    <Trans>Send email</Trans>
                </FormSubmitButton>
            </WHookForm>

            <BackButton action={onBack} />

            {error && (
                <div className="react-aria-FieldError pt-2">
                    <b>
                        <Trans>Error</Trans>: {error}
                    </b>
                </div>
            )}
        </LoginBox>
    );
}

function StandardLoginBox({
    provider,
    onForgotPassword,
    children,
    isSignup,
    setIsSignup,
}: {
    provider: AuthProvider;
    onForgotPassword: () => void;
    isSignup: boolean;
    setIsSignup: (isSignup: boolean) => void;
    children: ReactNode;
}) {
    const { t } = useLingui();
    const languageEnabled = useAtomValue(languagesEnabledAtom);
    const { loginGoogleButton } = useThemeProps();

    return (
        <LoginBox title={isSignup ? t`Sign up` : t`Log in`}>
            {children}

            <div className={L.Border}>
                <div className={L.Or}>
                    <Trans>Or</Trans>
                </div>
            </div>

            <WButton
                label={isSignup ? t`Sign up with Google` : t`Log in with Google`}
                size="lg"
                icon="jp-google-icon"
                fontSize="font-size-subtitle"
                className={cn(L.Button, L.Google)}
                action={async () =>
                    await provider.socialLogin({ connectionId: "google-oauth2", redirectPath: getCurrentPath() })
                }
                {...loginGoogleButton}
            />

            {!isSignup && (
                <div className="pt-5 pb-1 text-center">
                    <Link onPress={onForgotPassword}>
                        <Trans>Forgot password</Trans>?
                    </Link>
                </div>
            )}

            <div className="pt-5 text-center">
                {isSignup && (
                    <div className="mb-2">
                        <Trans>Already have an account?</Trans>
                    </div>
                )}
                <WButton
                    label={isSignup ? t`Log in` : t`Create an account`}
                    className={cn(L.More)}
                    action={() => setIsSignup(!isSignup)}
                />
            </div>

            {languageEnabled && (
                <div className="pt-5 text-center">
                    <LocaleFullSelector />
                </div>
            )}

            <div className={L.Certs}>
                <IsoCerts />
            </div>
        </LoginBox>
    );
}

function WaitingForEmailConfirmation({
    email,
    onBack,
    mode,
}: {
    email: string;
    onBack: () => void;
    mode: "verify_email" | "reset_password";
}) {
    const { t } = useLingui();
    return (
        <LoginBox title={t`Confirm email`}>
            <div className={cn(L.Email, "text-center")}>
                <div>
                    <MaskIcon className="jp-mail-icon size-2rem" />
                </div>
                <p>{email}</p>
            </div>

            <p className="text-center">
                {mode === "verify_email"
                    ? t`Confirm your email address by clicking the verification link we just sent to your inbox.`
                    : t`Change your password by clicking the verification link we just sent to your inbox.`}
            </p>

            <BackButton action={onBack} />
        </LoginBox>
    );
}

type LoginUiStateType =
    | "login"
    | "signup"
    | "forgot_password"
    | "waiting_signup_confirmation"
    | "waiting_forgot_confirmation";

const username_param = "username";

export function LoginScreen({ apiKeyOnly }: { apiKeyOnly?: boolean }) {
    //#region Hooks
    const store = useStore();
    const queryParams = useMemo(() => new URLSearchParams(window.location.search), []);

    const defaultUsername = queryParams.get(username_param) ?? undefined;

    // Default to sign up with referral code
    const [uiState, setUiState] = useState<LoginUiStateType>(store.get(didStoreReferralCodeAtom) ? "signup" : "login");
    const [confirmationEmail, setConfirmationEmail] = useState("");

    const provider = useAtomValue(authProviderAtom);
    apiKeyOnly ??= !!provider.isFake;
    //#endregion

    let displayElement: ReactElement;

    switch (uiState) {
        case "forgot_password":
            displayElement = (
                <ForgotPassword
                    provider={provider}
                    onBack={() => setUiState("login")}
                    onPostSubmit={(username) => {
                        setConfirmationEmail(username);
                        setUiState("waiting_forgot_confirmation");
                    }}
                />
            );
            break;

        case "waiting_signup_confirmation":
        case "waiting_forgot_confirmation":
            displayElement = (
                <WaitingForEmailConfirmation
                    email={confirmationEmail}
                    onBack={() => setUiState("login")}
                    mode={uiState === "waiting_signup_confirmation" ? "verify_email" : "reset_password"}
                />
            );
            break;

        default: {
            displayElement = (
                <StandardLoginBox
                    provider={provider}
                    onForgotPassword={() => setUiState("forgot_password")}
                    isSignup={uiState === "signup"}
                    setIsSignup={(isSignup) => setUiState(isSignup ? "signup" : "login")}
                >
                    {!apiKeyOnly ? (
                        <Auth0LoginForm
                            defaultUsername={defaultUsername}
                            isSignup={uiState === "signup"}
                            onPostSignup={({ email, emailVerified }) => {
                                setConfirmationEmail(email ?? "");
                                setUiState(emailVerified ? "login" : "waiting_signup_confirmation");
                            }}
                        />
                    ) : (
                        <ApiKeyLoginForm />
                    )}
                </StandardLoginBox>
            );
        }
    }

    return (
        <LoginLayout>
            <Suspense>
                {/* NB: Chat is *not* loaded in Auth0 callback page, it will only be instantiated in the logged-in layout, to make sure the user data is available before the chat provider is loaded. */}
                <WChatLoader />
            </Suspense>
            {displayElement}
        </LoginLayout>
    );
}

/** Pass `?success=false` in URL to show failure */
export function AfterEmailVerification({ isSuccess, email }: { isSuccess?: boolean; email?: string }): ReactElement {
    //#region Hooks
    const router = useRouter();
    const queryParams = useMemo(() => new URLSearchParams(window.location.search), []);
    //#endregion

    isSuccess ??= queryParams.get("success") !== "false";

    const loginEmail = email ?? queryParams.get("email");
    const loginSearchParams = new URLSearchParams({
        ...(loginEmail ? { [username_param]: loginEmail } : {}),
    });

    return (
        <LoginLayout>
            <AfterEmailVerificationContent
                isSuccess={isSuccess}
                action={async () => await router.navigate({ to: "/", search: Object.fromEntries(loginSearchParams) })}
            />
        </LoginLayout>
    );
}

function AfterEmailVerificationContent({ isSuccess, action }: { isSuccess: boolean; action: Action }) {
    const { t } = useLingui();
    return (
        <LoginBox title={isSuccess ? t`Email verified` : t`Email verification failed`}>
            <div className="VStack items-center gap-4">
                <div className={cn("p-4 b b-solid", isSuccess ? "b-success" : "b-error")}>
                    <div className="text-center">
                        <MaskIcon className="jp-mail-icon size-2rem" />
                    </div>
                    <p className="text-center">
                        {isSuccess ? (
                            <Trans>Your email address has been successfully verified. You can now log in.</Trans>
                        ) : (
                            <Trans>There was an error verifying your email address.</Trans>
                        )}
                    </p>
                </div>
                <WButton color="primary" action={action} label={t`Continue`} />
            </div>
        </LoginBox>
    );
}
